/**
 * API Client Factory
 *
 * Creates configured Axios instances with consistent interceptors
 * for authentication, error handling, and token refresh
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { STORAGE_KEYS } from '../config';
import { triggerServerError } from '@/contexts/ServerErrorContext';

export interface ApiClientConfig {
  baseURL: string;
  timeout?: number;
  withRefreshToken?: boolean;
  refreshEndpoint?: string;
}

// Validation error detail from backend
export interface ValidationErrorDetail {
  valid: boolean;
  field: string;
  message: string;
  rowNumber: number;
}

export interface ErrorResponse {
  message: string;
  error?: string;
  status?: number;
  details?: Record<string, string[]> | ValidationErrorDetail[];
  timestamp?: string;
  path?: string;
  errors?: Record<string, string[]>; // Legacy format support
}

/**
 * Create an API client with standardized configuration
 *
 * @param config - Client configuration
 * @returns Configured Axios instance
 */
export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const {
    baseURL,
    timeout = 30000,
    withRefreshToken = true,
    refreshEndpoint = '/refresh-token',
  } = config;

  // Create axios instance
  const client = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  // Request interceptor - add authentication token
  client.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

      if (token && requestConfig.headers) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }

      return requestConfig;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle errors and token refresh
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError<ErrorResponse>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Handle 401 Unauthorized
      if (error.response?.status === 401 && originalRequest) {
        // Exclude auth endpoints from session timeout redirect
        // (login/register errors should be shown, not redirect to timeout)
        const authEndpoints = ['/login', '/register', '/forgot-password', '/reset-password'];
        const requestUrl = originalRequest.url || '';
        const isAuthEndpoint = authEndpoints.some(endpoint => requestUrl.includes(endpoint));

        if (!isAuthEndpoint) {
          // Attempt token refresh if enabled and not already retried
          if (withRefreshToken && !originalRequest._retry) {
            originalRequest._retry = true;
            const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

            if (refreshToken) {
              try {
                // Attempt to refresh the access token
                const response = await axios.post(
                  `${baseURL}${refreshEndpoint}`,
                  { refreshToken },
                  {
                    headers: {
                      'Content-Type': 'application/json',
                    },
                  }
                );

                const { accessToken } = response.data;
                localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);

                // Retry the original request with the new token
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                }

                return client(originalRequest);
              } catch (refreshError) {
                // Refresh failed - redirect to session timeout
                clearAuthDataAndRedirect();
                return Promise.reject(refreshError);
              }
            }
          }

          // No refresh token or refresh disabled - redirect to session timeout
          clearAuthDataAndRedirect();
          return Promise.reject(formatError(error));
        }
      }

      // Handle 5xx Server Errors - show user-friendly dialog
      if (error.response?.status && error.response.status >= 500 && error.response.status < 600) {
        // Trigger the server error dialog
        triggerServerError();
      }

      // Format and reject the error
      return Promise.reject(formatError(error));
    }
  );

  return client;
}

/**
 * Clear authentication data and redirect to session timeout page
 */
function clearAuthDataAndRedirect(): void {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

  // Redirect to session timeout page
  window.location.href = '/session-timeout';
}

/**
 * Format Axios error into a consistent Error object with additional properties
 *
 * @param error - Axios error
 * @returns Formatted Error object
 */
function formatError(error: AxiosError<ErrorResponse>): Error {
  if (error.response) {
    // Server responded (with or without body)
    const errorData = error.response.data;
    const errorMessage = errorData?.message || 'An error occurred';

    // Create error with additional properties
    const formattedError = new Error(errorMessage) as Error & {
      code?: string;
      status?: number;
      details?: Record<string, string[]>;
      timestamp?: string;
      path?: string;
      errors?: Record<string, string[]>;
    };

    formattedError.code = errorData?.error || error.code;
    formattedError.status = error.response.status;
    formattedError.details = errorData?.details;
    formattedError.timestamp = errorData?.timestamp;
    formattedError.path = errorData?.path;
    formattedError.errors = errorData?.errors;

    return formattedError;
  } else if (error.request) {
    // Request made but no response from server
    const networkError = new Error('No response from server') as Error & {
      code?: string;
      status?: number;
    };
    networkError.code = error.code;
    networkError.status = 0;
    return networkError;
  } else {
    // Error in request setup
    return new Error(error.message || 'Request failed');
  }
}

/**
 * Helper to create a client for multipart/form-data requests
 */
export function createMultipartClient(config: ApiClientConfig): AxiosInstance {
  const client = createApiClient(config);

  // Override content-type for multipart requests
  client.defaults.headers['Content-Type'] = 'multipart/form-data';

  return client;
}
